'use client'

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  isLoading?: boolean
}

/**
 * Build a list of page numbers to display, with ellipsis gaps.
 * Always shows first, last, and up to 2 pages around the current page.
 * Example: [1, '...', 4, 5, 6, '...', 25]
 */
function getPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  const pages: (number | '...')[] = []
  const rangeStart = Math.max(2, current - 1)
  const rangeEnd = Math.min(total - 1, current + 1)

  pages.push(1)

  if (rangeStart > 2) {
    pages.push('...')
  }

  for (let i = rangeStart; i <= rangeEnd; i++) {
    pages.push(i)
  }

  if (rangeEnd < total - 1) {
    pages.push('...')
  }

  pages.push(total)

  return pages
}

export function Pagination({ currentPage, totalPages, onPageChange, isLoading }: PaginationProps) {
  if (totalPages <= 1) return null

  const pageNumbers = getPageNumbers(currentPage, totalPages)

  const btnBase =
    'inline-flex items-center justify-center h-8 min-w-[2rem] rounded-md text-sm transition-colors'
  const btnDisabled = 'disabled:opacity-40 disabled:cursor-not-allowed'
  const btnHover = 'hover:bg-[var(--color-surface-hover)]'

  return (
    <div className="flex items-center justify-center px-2 py-3">
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1 || isLoading}
          className={`${btnBase} w-8 ${btnDisabled} ${btnHover}`}
        >
          <ChevronLeftIcon className="w-4 h-4" />
        </button>

        {pageNumbers.map((page, idx) =>
          page === '...' ? (
            <span
              key={`ellipsis-${idx}`}
              className="inline-flex items-center justify-center h-8 w-8 text-sm text-[var(--color-text-muted)]"
            >
              ...
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              disabled={isLoading}
              className={`${btnBase} px-1 ${btnDisabled} ${
                page === currentPage
                  ? 'bg-[var(--color-surface-hover)] font-semibold'
                  : btnHover
              }`}
            >
              {page}
            </button>
          )
        )}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages || isLoading}
          className={`${btnBase} w-8 ${btnDisabled} ${btnHover}`}
        >
          <ChevronRightIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
